/**
 * DNS instructions modal component.
 * Shows DNS configuration instructions after domain creation.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading';
import { Copy, Check, X, ExternalLink } from 'lucide-react';
import type { DomainResponse, DomainStatusResponse } from '@/api/schemas';
import { useDomainStatus } from '@/hooks/useDomains';
import './dns-instructions-modal.scss';

export interface DNSInstructionsModalProps {
  domain: DomainResponse;
  onVerify: () => Promise<void>;
  onClose: () => void;
  isVerifying?: boolean;
  verificationStatus?: DomainStatusResponse;
}

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  ttl?: number;
}

/**
 * Detects DNS provider from domain name (basic heuristic).
 */
function detectDNSProvider(domainName: string): string | null {
  const lowerDomain = domainName.toLowerCase();
  if (lowerDomain.includes('cloudflare')) return 'cloudflare';
  if (lowerDomain.includes('godaddy')) return 'godaddy';
  if (lowerDomain.includes('aws') || lowerDomain.includes('amazon')) return 'route53';
  return null;
}

/**
 * Formats DNS record for display.
 */
function formatDNSRecord(record: DNSRecord, dnsMethod: string): string {
  if (dnsMethod === 'CNAME') {
    return `Type:     CNAME
Name:     ${record.name}
Target:   ${record.value}
TTL:      300 seconds (5 minutes)
Proxy:    OFF (DNS-only)`;
  } else {
    // NS method - show both NS records
    return `Type:     NS
Name:     ${record.name}
Value:    ${record.value}
TTL:      300 seconds (5 minutes)`;
  }
}

/**
 * Gets provider-specific tips.
 */
function getProviderTips(provider: string | null): string {
  switch (provider) {
    case 'cloudflare':
      return 'Ensure proxy is OFF (grey cloud)';
    case 'godaddy':
      return 'Add record in DNS Management → Records';
    case 'route53':
      return 'Create record in Hosted Zone';
    default:
      return "Add record in your DNS provider's control panel";
  }
}

/**
 * Gets provider-specific link.
 */
function getProviderLink(provider: string | null, domainName: string): string | null {
  // Extract root domain for links
  const parts = domainName.split('.');
  const rootDomain = parts.slice(-2).join('.');
  
  switch (provider) {
    case 'cloudflare':
      return `https://dash.cloudflare.com/?to=/:account/dns/${rootDomain}`;
    case 'godaddy':
      return `https://dcc.godaddy.com/manage/${rootDomain}/dns`;
    case 'route53':
      return `https://console.aws.amazon.com/route53/v2/hostedzones#ListRecordSets/${rootDomain}`;
    default:
      return null;
  }
}

export function DNSInstructionsModal({
  domain,
  onVerify,
  onClose,
  isVerifying = false,
  verificationStatus,
}: DNSInstructionsModalProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { data: statusData } = useDomainStatus(domain.domain_id);
  const provider = detectDNSProvider(domain.domain_name);
  const providerLink = getProviderLink(provider, domain.domain_name);

  // Extract subdomain label (e.g., "go" from "go.example.com")
  const subdomainLabel = domain.domain_name.split('.')[0];

  // Get DNS records from status response or use defaults
  const statusRecords = verificationStatus?.required_records || statusData?.required_records;
  const records: DNSRecord[] =
    statusRecords && statusRecords.length > 0
      ? (statusRecords as DNSRecord[])
      : domain.dns_method === 'CNAME'
        ? [
            {
              type: 'CNAME',
              name: subdomainLabel,
              value: 'edge.origynflow.com',
              ttl: 300,
            },
          ]
        : [
            {
              type: 'NS',
              name: subdomainLabel,
              value: 'ns1.origynflow.com',
              ttl: 300,
            },
            {
              type: 'NS',
              name: subdomainLabel,
              value: 'ns2.origynflow.com',
              ttl: 300,
            },
          ];

  const handleCopy = async (record: DNSRecord, recordIndex: number) => {
    const recordText = formatDNSRecord(record, domain.dns_method || 'CNAME');
    try {
      await navigator.clipboard.writeText(recordText);
      setCopiedIndex(recordIndex);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = recordText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedIndex(recordIndex);
        setTimeout(() => setCopiedIndex(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const isVerified = statusData?.verified || verificationStatus?.verified || false;

  // Auto-close modal if verified
  useEffect(() => {
    if (isVerified) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isVerified, onClose]);

  return (
    <div className="dns-instructions-modal" role="dialog" aria-labelledby="dns-modal-title">
      <div className="dns-instructions-modal__backdrop" onClick={onClose} />
      <div className="dns-instructions-modal__content">
        <div className="dns-instructions-modal__header">
          <h2 id="dns-modal-title" className="h3">
            Configure DNS for {domain.domain_name}
          </h2>
          <p className="subtitle">Follow these steps to connect your domain</p>
          <button
            className="dns-instructions-modal__close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X />
          </button>
        </div>

        <div className="dns-instructions-modal__body">
          {/* DNS Record Instructions */}
          <section className="dns-instructions-modal__section">
            <h3 className="h4">DNS Record Instructions</h3>
            {domain.dns_method === 'CNAME' ? (
              <div className="dns-instructions-modal__record">
                <p className="dns-instructions-modal__record-label">
                  Add this CNAME record to your DNS:
                </p>
                <div className="dns-instructions-modal__code-block">
                  <pre>
                    {formatDNSRecord(records[0], 'CNAME')}
                  </pre>
                  <button
                    className="dns-instructions-modal__copy"
                    onClick={() => handleCopy(records[0], 0)}
                    aria-label="Copy DNS record"
                  >
                    {copiedIndex === 0 ? <Check /> : <Copy />}
                    {copiedIndex === 0 ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="dns-instructions-modal__record">
                <p className="dns-instructions-modal__record-label">
                  Add these NS records to your DNS:
                </p>
                {records.map((record, idx) => (
                  <div key={idx} className="dns-instructions-modal__code-block">
                    <pre>
                      {formatDNSRecord(record, 'NS')}
                    </pre>
                    <button
                      className="dns-instructions-modal__copy"
                      onClick={() => handleCopy(record, idx)}
                      aria-label={`Copy NS record ${idx + 1}`}
                    >
                      {copiedIndex === idx ? <Check /> : <Copy />}
                      {copiedIndex === idx ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Provider-Specific Tips */}
          <section className="dns-instructions-modal__section">
            <h3 className="h4">Provider-Specific Tips</h3>
            <p className="dns-instructions-modal__tip">{getProviderTips(provider)}</p>
            {providerLink && (
              <a
                href={providerLink}
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__link"
              >
                <ExternalLink />
                Open {provider === 'cloudflare' ? 'Cloudflare' : provider === 'godaddy' ? 'GoDaddy' : 'Route53'}
              </a>
            )}
          </section>

          {/* DNS Provider Help Articles */}
          <section className="dns-instructions-modal__section">
            <h3 className="h4">Need Help?</h3>
            <p className="dns-instructions-modal__tip" style={{ marginBottom: 'var(--of-space-3)' }}>
              Follow these guides for your DNS provider:
            </p>
            <div className="dns-instructions-modal__help-links">
              <a
                href="https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                Cloudflare DNS Guide
              </a>
              <a
                href="https://www.godaddy.com/help/add-a-cname-record-19232"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                GoDaddy DNS Guide
              </a>
              <a
                href="https://www.namecheap.com/support/knowledgebase/article.aspx/9645/2237/how-do-i-add-a-cname-record-for-my-domain/"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                Namecheap DNS Guide
              </a>
              <a
                href="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/resource-record-sets-creating.html"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                AWS Route53 Guide
              </a>
              <a
                href="https://support.google.com/domains/answer/3290350"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                Google Domains Guide
              </a>
              <a
                href="https://www.name.com/support/articles/115004895548-Adding-DNS-records"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                Name.com DNS Guide
              </a>
              <a
                href="https://www.hover.com/help/answer/how-do-i-add-dns-records"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                Hover DNS Guide
              </a>
              <a
                href="https://help.dreamhost.com/hc/en-us/articles/214694348-Basic-DNS-records"
                target="_blank"
                rel="noopener noreferrer"
                className="dns-instructions-modal__help-link"
              >
                <ExternalLink />
                DreamHost DNS Guide
              </a>
            </div>
          </section>

          {/* Verification Status */}
          {!isVerified && (
            <section className="dns-instructions-modal__section">
              <h3 className="h4">Verification Status</h3>
              <div className="dns-instructions-modal__verification-status">
                <p className="dns-instructions-modal__tip">
                  <strong>Current Status:</strong> DNS records not verified
                </p>
                <p className="dns-instructions-modal__tip">
                  We're checking for the following records:
                </p>
                <ul className="dns-instructions-modal__check-list">
                  {records.map((record, idx) => (
                    <li key={idx}>
                      <strong>{record.type}</strong> record: <code>{record.name}</code> → <code>{record.value}</code>
                    </li>
                  ))}
                </ul>
                {verificationStatus && !verificationStatus.verified && (
                  <p className="dns-instructions-modal__tip" style={{ marginTop: 'var(--of-space-3)', fontWeight: 600 }}>
                    Verification failed. Please check that the DNS records above are correctly configured.
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Verification Instructions */}
          <section className="dns-instructions-modal__section">
            <h3 className="h4">Verification</h3>
            <p className="dns-instructions-modal__tip">
              After adding the DNS record, click 'Verify DNS' below. Verification usually takes 1-3 minutes.
            </p>
            <p className="dns-instructions-modal__note">
              If verification fails, wait 5-10 minutes for DNS propagation and try again.
            </p>
          </section>

          {/* Verification Status */}
          {isVerified && (
            <div className="dns-instructions-modal__success alert alert--success">
              <Check />
              <div>
                <p className="alert__title">Domain Verified!</p>
                <p>Your domain is now active and ready to use.</p>
              </div>
            </div>
          )}
        </div>

        <div className="dns-instructions-modal__footer">
          {!isVerified && (
            <Button
              onClick={onVerify}
              disabled={isVerifying}
              className="dns-instructions-modal__verify"
            >
              {isVerifying ? (
                <>
                  <LoadingSpinner size="sm" />
                  Verifying...
                </>
              ) : (
                'Verify DNS'
              )}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {isVerified ? 'Close' : 'Skip for Now'}
          </Button>
        </div>
      </div>
    </div>
  );
}

